// The account's timezone (shared/API.md §41.9): Mattermost keeps it on the user as
// `timezone = {useAutomaticTimezone: "true"|"false", automaticTimezone: <IANA>, manualTimezone: <IANA>}`
// (every value a STRING, model.User.Timezone is map[string]string). Pantalla edits it; "Automática"
// keeps `automaticTimezone` in step with the device on every launch.

export interface AccountTimezone {
  useAutomatic: boolean;
  automatic: string;
  manual: string;
}

/** Reads the user's `timezone` map; an absent map is the server's default (automatic, no zone yet). */
export function decodeTimezone(raw: unknown): AccountTimezone {
  const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const s = (v: unknown) => (typeof v === 'string' ? v : '');
  const flag = o.useAutomaticTimezone;
  // Absent or anything but an explicit "false" reads as automatic, like the webapp.
  const useAutomatic = !(flag === 'false' || flag === false);
  return { useAutomatic, automatic: s(o.automaticTimezone), manual: s(o.manualTimezone) };
}

/** The wire map for `PUT /users/me/patch {timezone}` — strings only. */
export function encodeTimezone(tz: AccountTimezone): Record<string, string> {
  return { useAutomaticTimezone: tz.useAutomatic ? 'true' : 'false', automaticTimezone: tz.automatic, manualTimezone: tz.manual };
}

/** The zone the account says the reader lives in (manual when chosen, else the device's). */
export function effectiveZone(tz: AccountTimezone): string {
  return tz.useAutomatic ? tz.automatic : tz.manual || tz.automatic;
}

/**
 * On launch: the patch that keeps an automatic account in step with this device, or null when
 * nothing needs writing (manual accounts are left alone; an unknown device zone writes nothing).
 */
export function automaticSync(tz: AccountTimezone, deviceZone: string | null | undefined): AccountTimezone | null {
  if (!tz.useAutomatic || !deviceZone || tz.automatic === deviceZone) return null;
  return { ...tz, automatic: deviceZone };
}

/**
 * The picker's list: the runtime's IANA zones (Intl.supportedValuesOf) with the current choices
 * guaranteed present, America/Mexico_City first (the company's zone), then alphabetical.
 */
export function timezoneOptions(supported: readonly string[], ensure: ReadonlyArray<string | null | undefined>): string[] {
  const set = new Set(supported.filter(Boolean));
  for (const z of ensure) if (z) set.add(z);
  set.add('America/Mexico_City');
  const rest = [...set].filter((z) => z !== 'America/Mexico_City').sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  return ['America/Mexico_City', ...rest];
}

/** «America/Mexico_City» → «Mexico City (America)» — the list's readable label. */
export function timezoneLabel(zone: string): string {
  const parts = zone.split('/');
  if (parts.length < 2) return zone.replace(/_/g, ' ');
  const city = parts.slice(1).join(' / ').replace(/_/g, ' ');
  return `${city} (${parts[0].replace(/_/g, ' ')})`;
}
